"use client"

import { useTranslations } from "next-intl"
import Link from "next/link"
import { Check } from "lucide-react"
import { DockSheet } from "@/components/ui/dock-sheet"
import { DialogCard } from "@/components/ui/dialog-card"
import { Button } from "@/components/ui/button"
import { useMobile } from "@/hooks/use-mobile"
import { cn } from "@/lib/utils"
import { creationFlowPrimaryCtaClass } from "@/lib/creation-flow-form-styles"

type MessagingGuidelinesSheetProps = {
  open: boolean
  onAccept: () => void | Promise<void>
  onGoBack: () => void
}

function GuidelinesBody() {
  const tChat = useTranslations("chat.guidelines")

  const items = [
    tChat("item1"),
    tChat("item2"),
    tChat("item3"),
    tChat("item4Prefix"),
  ] as const

  return (
    <div className="space-y-4 text-sm leading-relaxed text-foreground/85">
      <p>{tChat("intro")}</p>
      <ul className="space-y-3">
        {items.map((item, index) => (
          <li key={index} className="flex items-start gap-3">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" strokeWidth={2.5} />
            <span>
              {index === 3 ? (
                <>
                  {item}{" "}
                  <Link href="/community-guidelines" className="underline underline-offset-2">
                    {tChat("communityLink")}
                  </Link>{" "}
                  {tChat("and")}{" "}
                  <Link href="/terms" className="underline underline-offset-2">
                    {tChat("termsLink")}
                  </Link>
                  .
                </>
              ) : (
                item
              )}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

export function MessagingGuidelinesSheet({ open, onAccept, onGoBack }: MessagingGuidelinesSheetProps) {
  const tChat = useTranslations("chat.guidelines")
  const isMobile = useMobile()

  const footer = (
    <div className="flex gap-2">
      <Button
        type="button"
        variant="outline"
        className="h-12 flex-1 rounded-full border-foreground/20 bg-transparent text-base text-foreground hover:bg-foreground/10"
        onClick={onGoBack}
      >
        {tChat("goBack")}
      </Button>
      <Button type="button" className={cn("h-12 flex-1 rounded-full text-base", creationFlowPrimaryCtaClass)} onClick={() => void onAccept()}>
        {tChat("iAgree")}
      </Button>
    </div>
  )

  if (!isMobile) {
    return (
      <DialogCard
        open={open}
        onOpenChange={(next) => { if (!next) onGoBack() }}
        title={tChat("title")}
        panelClassName="max-w-md h-[min(90dvh,720px)] max-h-[min(90dvh,720px)]"
        footer={footer}
      >
        <GuidelinesBody />
      </DialogCard>
    )
  }

  return (
    <DockSheet open={open} onOpenChange={(next) => { if (!next) onGoBack() }} title={tChat("title")} footer={footer}>
      <GuidelinesBody />
    </DockSheet>
  )
}
